(() => {
  'use strict';

  const PAGE_CACHE = new Map();
  const PAGE_CACHE_TTL = 30000;

  const BUNDLE_ID_PROPERTY = '_RunIt bundle ID';
  const BUNDLE_ROLE_PROPERTY = '_RunIt bundle role';
  const DISCOUNT_CODE_PROPERTY = '_RunIt bundle discount code';

  let activeQuickAdd = null;

  function normalizeCode(code) {
    return String(code || '').trim().toUpperCase();
  }

  function getRootRoute() {
    return (
      window.Shopify?.routes?.root ||
      window.theme?.routes?.root ||
      '/'
    );
  }

  function getContext(button) {
    if (!button) return null;

    const component = button.closest('quick-add-product');
    const holder = button.closest('[data-quick-add-holder]');
    const productItem = button.closest('[data-grid-item]');

    if (!component || !holder) return null;

    let handle = button.getAttribute('data-quick-add-modal-handle');

    if (!handle) {
      const productLink = productItem?.querySelector('a[href*="/products/"]');

      if (productLink) {
        try {
          const url = new URL(productLink.href, window.location.origin);
          const match = url.pathname.match(/\/products\/([^/?#]+)/);

          if (match) {
            handle = decodeURIComponent(match[1]);
          }
        } catch (error) {}
      }
    }

    if (!handle) return null;

    return {
      button,
      component,
      holder,
      productItem,
      handle,
      productId: holder.getAttribute('data-quick-add-holder'),
      productUrl:
        getRootRoute() +
        'products/' +
        encodeURIComponent(handle)
    };
  }

  function loadProductPage(context) {
    const cacheKey = context.productUrl;
    const now = Date.now();
    const cached = PAGE_CACHE.get(cacheKey);

    if (
      cached &&
      now - cached.timestamp < PAGE_CACHE_TTL
    ) {
      return cached.promise;
    }

    if (cached) {
      PAGE_CACHE.delete(cacheKey);
    }

    const promise = fetch(context.productUrl, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Could not load product page.');
        }

        return response.text();
      })
      .then((html) => parseProductPage(html))
      .catch((error) => {
        PAGE_CACHE.delete(cacheKey);
        throw error;
      });

    PAGE_CACHE.set(cacheKey, {
      timestamp: now,
      promise
    });

    return promise;
  }

  function parseProductPage(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const propertyBlocks = [];
    const seen = new Set();

    doc
      .querySelectorAll(
        'input[name^="properties["]:not([type="hidden"]), ' +
          'select[name^="properties["], ' +
          'textarea[name^="properties["]'
      )
      .forEach((field) => {
        const block = field.closest('.product__block');

        if (
          !block ||
          block.matches('[data-runit-bundle]') ||
          seen.has(block)
        ) {
          return;
        }

        seen.add(block);
        propertyBlocks.push(block.outerHTML);
      });

    const bundle = doc.querySelector('[data-runit-bundle]');
    let bundleHTML = '';
    let bundleStyle = '';

    if (bundle) {
      bundleHTML = bundle.outerHTML;
      const bundleId = bundle.id;

      Array.from(doc.querySelectorAll('style')).some((style) => {
        if (
          bundleId &&
          style.textContent.includes(bundleId)
        ) {
          bundleStyle = style.textContent;
          return true;
        }

        return false;
      });
    }

    const hasBundle = !!bundle;
    const hasProperties = propertyBlocks.length > 0;

    return {
      hasBundle,
      hasProperties,
      needsChoiceDrawer: hasBundle || hasProperties,
      bundleHTML,
      bundleStyle,
      propertyBlocks
    };
  }

  function createElementFromHTML(html) {
    if (!html) return null;

    const template = document.createElement('template');
    template.innerHTML = html.trim();

    return template.content.firstElementChild;
  }

  function ensureModalTemplate(context) {
    const holder = context.holder;

    if (holder.querySelector('[data-quick-add-modal-template]')) {
      return;
    }

    const template = document.createElement('template');
    template.setAttribute('data-quick-add-modal-template', '');

    template.innerHTML = `
      <dialog
        class="drawer product-quick-add"
        data-product-id="${context.productId}"
        data-quick-add-modal
        data-scroll-lock-required
        inert
      >
        <form method="dialog">
          <button
            class="visually-hidden no-js"
            aria-label="Close"
          ></button>
        </form>

        <div
          class="drawer__inner product-quick-add__content"
          data-product-upsell-container
          data-scroll-lock-scrollable
          style="--swatch-size:var(--swatch-size-product)"
        >
          <div class="product-quick-add__close-outer">
            <button
              type="button"
              class="product-quick-add__close drawer__close"
              aria-label="Close"
              data-quick-add-modal-close
              autofocus
            >
              <svg
                aria-hidden="true"
                focusable="false"
                viewBox="0 0 24 24"
                width="24"
                height="24"
              >
                <path
                  d="M6.75 6.75 17.25 17.25M17.25 6.75 6.75 17.25"
                  fill="none"
                  stroke="currentColor"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>

          <div
            class="product-quick-add__inner"
            data-product-upsell-ajax
          ></div>

          <div class="loader loader--top">
            <div class="loader-indeterminate"></div>
          </div>
        </div>
      </dialog>
    `;

    holder.appendChild(template);
  }

  function findProductForm(modal) {
    if (!modal) return null;

    return (
      modal.querySelector('form[data-product-form]') ||
      modal.querySelector('product-form form') ||
      modal.querySelector('form[action*="/cart/add"]')
    );
  }

  function findDescriptionBlock(modal) {
    const direct = modal.querySelector(
      '[data-product-description], ' +
        '.product__block--description, ' +
        '.product__description'
    );

    if (direct) {
      return direct.closest('.product__block') || direct;
    }

    return (
      Array.from(modal.querySelectorAll('.product__block')).find((block) => {
        if (block.matches('[data-runit-bundle]')) return false;

        if (block.classList.contains('product__block--buttons')) {
          return false;
        }

        if (block.querySelector('[name^="properties["]')) {
          return false;
        }

        return !!block.querySelector('.rte');
      }) || null
    );
  }

  function injectProperties(modal, data, form, parent, anchor) {
    modal
      .querySelectorAll(
        'input[name^="properties["]:not([type="hidden"]), ' +
          'select[name^="properties["], ' +
          'textarea[name^="properties["]'
      )
      .forEach((field) => {
        const block = field.closest('.product__block');

        if (block) {
          block.setAttribute('data-runit-quick-add-property', '');
        }
      });

    const existingNames = new Set();

    modal
      .querySelectorAll(
        '[data-runit-quick-add-property] [name^="properties["]'
      )
      .forEach((field) => {
        const name = field.getAttribute('name');

        if (name) {
          existingNames.add(name);
        }
      });

    data.propertyBlocks.forEach((html) => {
      const block = createElementFromHTML(html);

      if (!block) return;

      const fields = block.querySelectorAll('[name^="properties["]');
      let hasNewField = false;

      fields.forEach((field) => {
        const name = field.getAttribute('name');

        if (
          name &&
          !existingNames.has(name)
        ) {
          hasNewField = true;
        }
      });

      if (!hasNewField) return;

      block.setAttribute('data-runit-quick-add-property', '');

      fields.forEach((field) => {
        field.setAttribute('form', form.id);

        const name = field.getAttribute('name');

        if (name) {
          existingNames.add(name);
        }
      });

      parent.insertBefore(block, anchor);
    });
  }

  function formatMoney(cents) {
    const amount = Number(cents || 0) / 100;

    try {
      return (
        'PHP ' +
        new Intl.NumberFormat('en-PH', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(amount)
      );
    } catch (error) {
      return 'PHP ' + amount.toFixed(2);
    }
  }

  function getBundleVariants(bundle) {
    if (!bundle) return [];

    const script = bundle.querySelector('[data-current-variants]');

    if (!script) return [];

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return [];
    }
  }

  function getCurrentVariant(form, bundle) {
    const input = form.querySelector('[name="id"]');
    const variantId = input ? Number(input.value) : null;
    const variants = getBundleVariants(bundle);

    const matched = variants.find(
      (variant) => Number(variant.id) === variantId
    );

    if (matched) return matched;

    if (variants.length) {
      return variants[0];
    }

    if (variantId) {
      return {
        id: variantId
      };
    }

    return null;
  }

  function getQuantity(form) {
    const qty = form.querySelector('[name="quantity"]');

    if (!qty) {
      return 1;
    }

    const value = parseInt(qty.value, 10);

    return value > 0 ? value : 1;
  }

  function getCurrentProductProperties(modal) {
    const properties = {};

    const fields = modal.querySelectorAll(
      '[data-runit-quick-add-property] [name^="properties["]'
    );

    fields.forEach((field) => {
      if (
        field.disabled ||
        field.type === 'file'
      ) {
        return;
      }

      if (
        field.type === 'checkbox' ||
        field.type === 'radio'
      ) {
        return;
      }

      const name = field.getAttribute('name');
      const match = name && name.match(/^properties\[(.+)\]$/);

      if (!match) return;

      if (
        field.value === null ||
        field.value === undefined ||
        String(field.value) === ''
      ) {
        return;
      }

      properties[match[1]] = String(field.value);
    });

    fields.forEach((field) => {
      if (field.disabled) {
        return;
      }

      if (
        field.type !== 'checkbox' &&
        field.type !== 'radio'
      ) {
        return;
      }

      if (!field.checked) {
        return;
      }

      const name = field.getAttribute('name');
      const match = name && name.match(/^properties\[(.+)\]$/);

      if (!match) {
        return;
      }

      properties[match[1]] = String(field.value);
    });

    return properties;
  }

  function selectedOfferCard(bundle) {
    if (!bundle) {
      return null;
    }

    const selected = bundle.querySelector(
      'input[type="radio"]:checked'
    );

    if (
      !selected ||
      selected.value === 'single'
    ) {
      return null;
    }

    return bundle.querySelector(
      '[data-offer-card="' +
        selected.value +
        '"]'
    );
  }

  function syncOfferVisibility(bundle) {
    if (!bundle) {
      return;
    }

    const selected = bundle.querySelector(
      'input[name^="runit-bundle-"]:checked'
    );

    const selectedValue = selected
      ? selected.value
      : 'single';

    bundle
      .querySelectorAll(
        '[data-offer-card]:not([data-offer-card="single"])'
      )
      .forEach((offer) => {
        const products = offer.querySelector(
          '[data-offer-products]'
        );

        if (!products) {
          return;
        }

        const show =
          offer.dataset.offerCard === selectedValue;

        products.hidden = !show;

        if (show) {
          products.removeAttribute('hidden');
        } else {
          products.setAttribute('hidden', '');
        }
      });
  }

  function updateBundlePrices(bundle, form) {
    if (!bundle) return;

    const variant = getCurrentVariant(
      form,
      bundle
    );

    if (
      !variant ||
      variant.price === undefined
    ) {
      return;
    }

    const singlePrice = bundle.querySelector(
      '[data-single-price]'
    );

    if (singlePrice) {
      singlePrice.textContent = formatMoney(
        variant.price
      );
    }

    bundle
      .querySelectorAll(
        '[data-current-line-price], [data-current-mini-price]'
      )
      .forEach((price) => {
        price.textContent = formatMoney(
          variant.price
        );
      });

    const singleCompare = bundle.querySelector(
      '[data-single-compare]'
    );

    if (singleCompare) {
      if (
        variant.compare_at_price &&
        variant.compare_at_price > variant.price
      ) {
        singleCompare.textContent = formatMoney(
          variant.compare_at_price
        );

        singleCompare.hidden = false;
      } else {
        singleCompare.hidden = true;
      }
    }

    bundle
      .querySelectorAll(
        '[data-offer-card]:not([data-offer-card="single"])'
      )
      .forEach((offer) => {
        let subtotal = Number(
          variant.price
        );

        offer
          .querySelectorAll(
            '[data-related-product]'
          )
          .forEach((item) => {
            subtotal += Number(
              item.dataset.price || 0
            );
          });

        const discount = Number(
          offer.dataset.discount || 0
        );

        const discounted = Math.max(
          0,
          subtotal - discount
        );

        const price = offer.querySelector(
          '[data-offer-price]'
        );

        const original = offer.querySelector(
          '[data-offer-original]'
        );

        if (price) {
          price.textContent = formatMoney(
            discounted
          );
        }

        if (original) {
          original.textContent = formatMoney(
            subtotal
          );
        }
      });
  }

  async function fetchCart() {
    const response = await fetch(
      getRootRoute() + 'cart.js',
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to load the cart.'
      );
    }

    return response.json();
  }

  function cartDiscountCodes(cart) {
    const codes = new Set(
      (cart.discount_codes || [])
        .map((discount) =>
          normalizeCode(discount.code)
        )
        .filter(Boolean)
    );

    const rememberApplication = (
      application
    ) => {
      if (
        !application ||
        application.type !== 'discount_code'
      ) {
        return;
      }

      const code = normalizeCode(
        application.title
      );

      if (code) {
        codes.add(code);
      }
    };

    (
      cart.cart_level_discount_applications ||
      []
    ).forEach(rememberApplication);

    (cart.items || []).forEach((item) => {
      (
        item.line_level_discount_allocations ||
        []
      ).forEach((allocation) => {
        rememberApplication(
          allocation.discount_application
        );
      });
    });

    return Array.from(codes);
  }

  function cartHasApplicableCodeDiscount(
    cart,
    code
  ) {
    const normalized = normalizeCode(code);

    if (!normalized) {
      return false;
    }

    const directMatch = (
      cart.discount_codes || []
    ).some((discount) => {
      return (
        normalizeCode(discount.code) ===
          normalized &&
        discount.applicable !== false
      );
    });

    if (directMatch) {
      return true;
    }

    const applicationMatches = (
      application
    ) => {
      return !!(
        application &&
        application.type ===
          'discount_code' &&
        normalizeCode(
          application.title
        ) === normalized
      );
    };

    if (
      (
        cart.cart_level_discount_applications ||
        []
      ).some(applicationMatches)
    ) {
      return true;
    }

    return (cart.items || []).some(
      (item) => {
        return (
          item.line_level_discount_allocations ||
          []
        ).some((allocation) =>
          applicationMatches(
            allocation.discount_application
          )
        );
      }
    );
  }

  function bundleLineDiscountTotal(
    cart,
    bundleId
  ) {
    if (!bundleId) return 0;

    return (cart.items || []).reduce(
      (total, item) => {
        const properties =
          item.properties || {};

        if (
          String(
            properties[
              BUNDLE_ID_PROPERTY
            ] || ''
          ) !== bundleId
        ) {
          return total;
        }

        return (
          total +
          Number(
            item.line_level_total_discount ||
              item.total_discount ||
              0
          )
        );
      },
      0
    );
  }

  async function postDiscountCodes(codes) {
    const response = await fetch(
      getRootRoute() + 'cart/update.js',
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type':
            'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          discount: codes.join(',')
        })
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to update the bundle discount.'
      );
    }

    return response.json();
  }

  async function applyBundleDiscount(
    code,
    bundleId,
    expectedDiscount
  ) {
    const normalized = normalizeCode(code);

    if (!normalized) {
      if (
        Number(expectedDiscount || 0) > 0
      ) {
        throw new Error(
          'The bundle discount is not configured.'
        );
      }

      return fetchCart();
    }

    const cart = await fetchCart();
    const codes = cartDiscountCodes(cart);

    if (!codes.includes(normalized)) {
      codes.push(normalized);
    }

    const updatedCart =
      await postDiscountCodes(codes);

    const expected = Math.max(
      1,
      Number(expectedDiscount || 0)
    );

    const beforeTotalDiscount = Number(
      cart.total_discount || 0
    );

    const afterTotalDiscount = Number(
      updatedCart.total_discount || 0
    );

    const bundleDiscount =
      bundleLineDiscountTotal(
        updatedCart,
        bundleId
      );

    const applied =
      cartHasApplicableCodeDiscount(
        updatedCart,
        normalized
      ) ||
      bundleDiscount >= expected ||
      afterTotalDiscount -
        beforeTotalDiscount >=
        expected;

    if (!applied) {
      throw new Error(
        'Shopify did not accept the bundle discount.'
      );
    }

    return updatedCart;
  }

  async function removeBundle(bundleId) {
    if (!bundleId) return;

    const cart = await fetchCart();
    const updates = {};

    (cart.items || []).forEach((item) => {
      const properties =
        item.properties || {};

      if (
        String(
          properties[
            BUNDLE_ID_PROPERTY
          ] || ''
        ) === bundleId
      ) {
        updates[item.key] = 0;
      }
    });

    if (!Object.keys(updates).length) {
      return;
    }

    const response = await fetch(
      getRootRoute() + 'cart/update.js',
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type':
            'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          updates
        })
      }
    );

    if (!response.ok) {
      throw new Error(
        'Unable to remove the failed bundle.'
      );
    }
  }

  function buildCartPayload(
    modal,
    bundle,
    form
  ) {
    const variant = getCurrentVariant(
      form,
      bundle
    );

    if (
      !variant ||
      !variant.id
    ) {
      return null;
    }

    const quantity = getQuantity(form);

    const offer = selectedOfferCard(
      bundle
    );

    const currentProperties =
      getCurrentProductProperties(
        modal
      );

    let bundleId = '';
    let discountCode = '';
    let expectedDiscount = 0;

    if (offer) {
      discountCode = normalizeCode(
        offer.dataset.discountCode
      );

      expectedDiscount = Number(
        offer.dataset.discount || 0
      );

      if (
        expectedDiscount > 0 &&
        !discountCode
      ) {
        return {
          error:
            'This bundle discount code is not configured. Please try again later.'
        };
      }

      bundleId =
        'RUNIT-QA-' +
        Date.now() +
        '-' +
        Math.random()
          .toString(36)
          .slice(2, 8);

      currentProperties[
        BUNDLE_ID_PROPERTY
      ] = bundleId;

      currentProperties[
        BUNDLE_ROLE_PROPERTY
      ] = 'Primary';

      if (discountCode) {
        currentProperties[
          DISCOUNT_CODE_PROPERTY
        ] = discountCode;
      }
    }

    const currentItem = {
      id: Number(variant.id),
      quantity
    };

    if (
      Object.keys(
        currentProperties
      ).length
    ) {
      currentItem.properties =
        currentProperties;
    }

    const sellingPlan =
      form.querySelector(
        '[name="selling_plan"]'
      );

    if (
      sellingPlan &&
      sellingPlan.value
    ) {
      currentItem.selling_plan =
        Number(
          sellingPlan.value
        );
    }

    const items = [
      currentItem
    ];

    if (offer) {
      offer
        .querySelectorAll(
          '[data-related-product]'
        )
        .forEach((item) => {
          const id = Number(
            item.dataset.variantId
          );

          if (!id) return;

          const properties = {
            [BUNDLE_ID_PROPERTY]:
              bundleId,
            [BUNDLE_ROLE_PROPERTY]:
              'Related'
          };

          if (discountCode) {
            properties[
              DISCOUNT_CODE_PROPERTY
            ] = discountCode;
          }

          items.push({
            id,
            quantity,
            properties
          });
        });
    }

    return {
      items,
      isBundle: !!offer,
      bundleId,
      discountCode,
      expectedDiscount
    };
  }

  function setButtonLoading(
    button,
    loading,
    isBundle
  ) {
    if (!button) return;

    const text = button.querySelector(
      '[data-add-to-cart-text]'
    );

    if (loading) {
      button.disabled = true;

      button.classList.add(
        'is-loading'
      );

      if (text) {
        if (
          !text.dataset
            .runitOriginalText
        ) {
          text.dataset
            .runitOriginalText =
            text.innerHTML;
        }

        text.textContent =
          isBundle
            ? 'ADDING BUNDLE...'
            : 'ADDING...';
      }
    } else {
      button.disabled = false;

      button.classList.remove(
        'is-loading'
      );

      if (
        text &&
        text.dataset
          .runitOriginalText
      ) {
        text.innerHTML =
          text.dataset
            .runitOriginalText;
      }
    }
  }

  function refreshBroadcastCart(
    button,
    response,
    rootRoute
  ) {
    button.dispatchEvent(
      new CustomEvent(
        'theme:product:add',
        {
          detail: {
            response,
            button
          },
          bubbles: true
        }
      )
    );

    const cartDrawer =
      document.querySelector(
        'cart-drawer'
      );

    const cartItems =
      document.querySelector(
        'cart-items'
      );

    const drawerCart =
      window.theme &&
      window.theme.settings &&
      window.theme.settings.cartType ===
        'drawer';

    if (
      cartDrawer &&
      cartItems &&
      drawerCart
    ) {
      document.dispatchEvent(
        new CustomEvent(
          'theme:cart:refresh',
          {
            bubbles: true
          }
        )
      );

      return;
    }

    window.location.href =
      rootRoute +
      'cart';
  }

  async function addRunItItems(
    modal,
    bundle,
    form,
    button
  ) {
    if (
      form.dataset.runitAdding ===
      'true'
    ) {
      return;
    }

    const payload =
      buildCartPayload(
        modal,
        bundle,
        form
      );

    if (!payload) {
      alert(
        'This product could not be added. Please try again.'
      );

      return;
    }

    if (payload.error) {
      alert(payload.error);
      return;
    }

    if (
      payload.isBundle &&
      payload.items.length < 2
    ) {
      alert(
        'The bundle could not be added. Please try again.'
      );

      return;
    }

    form.dataset.runitAdding =
      'true';

    setButtonLoading(
      button,
      true,
      payload.isBundle
    );

    const rootRoute =
      getRootRoute();

    let bundleAdded = false;

    try {
      const response = await fetch(
        rootRoute +
          'cart/add.js',
        {
          method: 'POST',
          credentials:
            'same-origin',
          headers: {
            'Content-Type':
              'application/json',
            Accept:
              'application/json',
            'X-Requested-With':
              'XMLHttpRequest'
          },
          body: JSON.stringify({
            items: payload.items
          })
        }
      );

      if (!response.ok) {
        const error =
          await response.json();

        throw error;
      }

      const data =
        await response.json();

      bundleAdded =
        payload.isBundle;

      if (payload.isBundle) {
        await applyBundleDiscount(
          payload.discountCode,
          payload.bundleId,
          payload.expectedDiscount
        );
      }

      form.dataset.runitAdding =
        'false';

      setButtonLoading(
        button,
        false,
        payload.isBundle
      );

      if (window.a11y) {
        window.a11y.lastElement =
          button;
      }

      refreshBroadcastCart(
        button,
        data,
        rootRoute
      );
    } catch (error) {
      if (
        bundleAdded &&
        payload.bundleId
      ) {
        try {
          await removeBundle(
            payload.bundleId
          );
        } catch (
          rollbackError
        ) {
          console.error(
            '[RunIt Quick Add] Rollback error:',
            rollbackError
          );
        }
      }

      console.error(
        '[RunIt Quick Add] Add error:',
        error
      );

      form.dataset.runitAdding =
        'false';

      setButtonLoading(
        button,
        false,
        payload.isBundle
      );

      alert(
        error.description ||
          error.message ||
          (
            payload.isBundle
              ? 'The bundle could not be added with its discount. Nothing was added. Please try again.'
              : 'The product could not be added. Please try again.'
          )
      );
    }
  }

  function setupRunItSubmission(
    modal,
    bundle,
    form
  ) {
    if (
      form.dataset
        .runitQuickAddReady ===
      'true'
    ) {
      return;
    }

    form.dataset
      .runitQuickAddReady =
      'true';

    const button =
      form.querySelector(
        '[data-add-to-cart]'
      );

    if (!button) return;

    if (bundle) {
      bundle
        .querySelectorAll(
          'input[type="radio"]'
        )
        .forEach((radio) => {
          radio.addEventListener(
            'change',
            () => {
              syncOfferVisibility(
                bundle
              );

              updateBundlePrices(
                bundle,
                form
              );
            }
          );
        });

      syncOfferVisibility(
        bundle
      );

      updateBundlePrices(
        bundle,
        form
      );
    }

    modal.addEventListener(
      'change',
      (event) => {
        if (
          bundle &&
          event.target &&
          event.target.name ===
            'id'
        ) {
          setTimeout(
            () => {
              updateBundlePrices(
                bundle,
                form
              );
            },
            50
          );
        }
      }
    );

    form.addEventListener(
      'submit',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        addRunItItems(
          modal,
          bundle,
          form,
          button
        );
      },
      true
    );
  }

  function injectBundle(
    modal,
    data,
    form,
    parent,
    anchor,
    context
  ) {
    if (
      !data.hasBundle ||
      !data.bundleHTML
    ) {
      return null;
    }

    const bundle =
      createElementFromHTML(
        data.bundleHTML
      );

    if (!bundle) {
      return null;
    }

    const oldId = bundle.id;

    const newId =
      oldId +
      '-quick-' +
      context.productId;

    bundle.id = newId;
    bundle.dataset.formId =
      form.id;

    parent.insertBefore(
      bundle,
      anchor
    );

    if (data.bundleStyle) {
      const style =
        document.createElement(
          'style'
        );

      style.setAttribute(
        'data-runit-quick-add-bundle-style',
        ''
      );

      style.textContent =
        oldId
          ? data.bundleStyle
              .split(oldId)
              .join(newId)
          : data.bundleStyle;

      modal.appendChild(style);
    }

    return bundle;
  }

  function injectEnhancements(
    context,
    data
  ) {
    if (
      !data.hasBundle &&
      !data.hasProperties
    ) {
      return;
    }

    const modal =
      context.component?.modal ||
      document.querySelector(
        '[data-quick-add-modal][open]'
      );

    if (!modal) return;

    const form =
      findProductForm(
        modal
      );

    if (!form) return;

    if (!form.id) {
      form.id =
        'runit-quick-add-form-' +
        context.productId +
        '-' +
        Date.now();
    }

    const description =
      findDescriptionBlock(
        modal
      );

    const buttonBlock =
      modal.querySelector(
        '.product__block--buttons'
      );

    let parent = null;
    let anchor = null;

    if (
      description &&
      description.parentNode
    ) {
      parent =
        description.parentNode;

      anchor =
        description;
    } else if (
      buttonBlock &&
      buttonBlock.parentNode
    ) {
      parent =
        buttonBlock.parentNode;

      anchor =
        buttonBlock;
    } else {
      parent =
        modal.querySelector(
          '.form__width'
        ) ||
        modal.querySelector(
          '[data-form-wrapper]'
        ) ||
        modal.querySelector(
          '[data-product-upsell-ajax]'
        );
    }

    if (!parent) return;

    injectProperties(
      modal,
      data,
      form,
      parent,
      anchor
    );

    const bundle =
      injectBundle(
        modal,
        data,
        form,
        parent,
        anchor,
        context
      );

    setupRunItSubmission(
      modal,
      bundle,
      form
    );
  }

  function nativeDirectAdd(
    button
  ) {
    if (!button) return;

    button.disabled = false;

    button.classList.remove(
      'is-loading'
    );

    if (window.a11y) {
      window.a11y.lastElement =
        button;
    }

    document.dispatchEvent(
      new CustomEvent(
        'theme:cart:add',
        {
          detail: {
            button
          }
        }
      )
    );
  }

  function openChoiceDrawer(
    context
  ) {
    const component =
      context.component;

    const button =
      context.button;

    ensureModalTemplate(
      context
    );

    component.modalButton =
      button;

    component.button =
      button;

    component.handle =
      context.handle;

    component.productId =
      context.productId;

    button.classList.add(
      'is-loading'
    );

    button.disabled = true;

    if (
      typeof component.renderModal ===
      'function'
    ) {
      component.renderModal();
    } else {
      nativeDirectAdd(
        button
      );
    }
  }

  document.addEventListener(
    'theme:quick-add:open',
    () => {
      const pending =
        activeQuickAdd;

      if (!pending) return;

      Promise.resolve(
        pending.pagePromise
      )
        .then((data) => {
          injectEnhancements(
            pending.context,
            data
          );

          activeQuickAdd =
            null;
        })
        .catch((error) => {
          console.warn(
            '[RunIt Quick Add] Drawer enhancement failed:',
            error
          );

          activeQuickAdd =
            null;
        });
    }
  );

  function prefetch(event) {
    const button =
      event.target.closest(
        'quick-add-product [data-add-to-cart], ' +
          'quick-add-product [data-quick-add-modal-handle]'
      );

    if (!button) return;

    const context =
      getContext(button);

    if (!context) return;

    loadProductPage(
      context
    ).catch(() => {});
  }

  document.addEventListener(
    'pointerover',
    prefetch,
    {
      passive: true
    }
  );

  document.addEventListener(
    'focusin',
    prefetch
  );

  document.addEventListener(
    'click',
    (event) => {
      const button =
        event.target.closest(
          'quick-add-product [data-add-to-cart], ' +
            'quick-add-product [data-quick-add-modal-handle]'
        );

      if (!button) return;

      const context =
        getContext(button);

      if (!context) return;

      const pagePromise =
        loadProductPage(
          context
        );

      activeQuickAdd = {
        context,
        pagePromise
      };

      if (
        button.hasAttribute(
          'data-quick-add-modal-handle'
        )
      ) {
        return;
      }

      if (
        !button.hasAttribute(
          'data-add-to-cart'
        )
      ) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      button.classList.add(
        'is-loading'
      );

      pagePromise
        .then((data) => {
          if (
            !data.needsChoiceDrawer
          ) {
            activeQuickAdd =
              null;

            nativeDirectAdd(
              button
            );

            return;
          }

          openChoiceDrawer(
            context
          );
        })
        .catch((error) => {
          console.warn(
            '[RunIt Quick Add] Choice check failed:',
            error
          );

          activeQuickAdd =
            null;

          nativeDirectAdd(
            button
          );
        });
    },
    true
  );
})();